/**
 * Where the agency gets paid.
 *
 * A repeater rather than a text box, because a payment page has to lay each field on its own
 * line and offer the account number for copying — and because an agency with a naira collection
 * account, a service-charge account and a dollar account needs all three, switchable.
 *
 * The field list comes from the currency (`@rentorng/core` `banking.ts`), and so does the
 * validation, so this form and the API cannot disagree about whether an account is complete.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CURRENCIES,
  bankAccountLines,
  bankFieldsFor,
  summariseBankAccount,
  supportedBankCurrencies,
  validateBankAccount,
  type BankFieldSpec,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { IconBank, IconPlus, IconTrash } from "./icons";
import { Alert, Badge, Button, Card, CardTitle, EmptyState, Field, Input, Select, Spinner, Textarea } from "./ui";

interface Account {
  id: string;
  label: string | null;
  currency: string;
  fields: Record<string, string>;
  enabled: boolean;
  instructions: string | null;
  sortOrder: number;
  purpose: Purpose;
}

/**
 * What an account is for.
 *
 * `collection` is where the agency's own clients pay them — the details a tenant is shown on a
 * payment page. `payout` is where we settle money collected on their behalf. Keeping them apart
 * matters more than it looks: one is published to strangers, the other is not, and a single list
 * of "bank accounts" is one mis-tick away from showing a payer the wrong one.
 */
type Purpose = "collection" | "payout";

const COPY: Record<Purpose, { title: string; lead: string; empty: string; add: string; live: (on: number, all: number) => string }> = {
  collection: {
    title: "How your clients pay you",
    lead: "The accounts your tenants and landlords transfer into. Every one switched on appears on your payment pages, whatever its currency.",
    empty: "Add the account your tenants transfer into. It appears on every invoice and on the pay link you send them.",
    add: "Add an account",
    live: (on, all) =>
      on === 0
        ? "Nothing is switched on, so your payment pages show nowhere to pay."
        : `${on} of ${all} shown to payers. Somebody paying in a currency you hold an account in sees that one.`,
  },
  payout: {
    title: "How we pay you",
    lead: "Where we settle money collected on your behalf. Never shown to a tenant — this account is between you and us.",
    empty: "Only needed once you collect through us. Until then, your clients pay you directly and nothing passes through us.",
    add: "Add a payout account",
    live: (on) => (on === 0 ? "No payout account yet." : "Where settlements are sent."),
  },
};

interface Draft {
  label: string;
  currency: string;
  fields: Record<string, string>;
  instructions: string;
  enabled: boolean;
}

const emptyDraft = (currency: string): Draft => ({
  label: "",
  currency,
  fields: {},
  instructions: "",
  enabled: true,
});

export function BankAccounts({
  readOnly,
  orgCurrency,
  purpose = "collection",
  step,
}: {
  readOnly: boolean;
  orgCurrency: string;
  purpose?: Purpose;
  /** Shown before the heading when the screen is a sequence rather than a single list. */
  step?: number;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bank-accounts"],
    queryFn: () => api.get<{ accounts: Account[]; currencies: string[] }>("/settings/bank-accounts"),
  });

  const save = useApiMutation({
    mutationFn: (input: { id: string | null; body: Record<string, unknown> }) =>
      input.id
        ? api.patch(`/settings/bank-accounts/${input.id}`, input.body)
        : api.post("/settings/bank-accounts", input.body),
    invalidates: [["bank-accounts"], ["settings"]],
    onSuccess: () => {
      setDraft(null);
      setEditingId(null);
      void refetch();
    },
  });

  const toggle = useApiMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api.patch(`/settings/bank-accounts/${input.id}`, { enabled: input.enabled }),
    invalidates: [["bank-accounts"]],
  });

  const remove = useApiMutation({
    mutationFn: (id: string) => api.delete(`/settings/bank-accounts/${id}`),
    invalidates: [["bank-accounts"]],
  });

  // Each instance shows only its own kind. The endpoint returns both because the page needs
  // both, and filtering here keeps that one request rather than two.
  const accounts = (data?.accounts ?? []).filter((account) => (account.purpose ?? "collection") === purpose);
  const enabledCount = accounts.filter((account) => account.enabled).length;

  const startEdit = (account: Account) => {
    setEditingId(account.id);
    setDraft({
      label: account.label ?? "",
      currency: account.currency,
      fields: { ...account.fields },
      instructions: account.instructions ?? "",
      enabled: account.enabled,
    });
  };

  const startNew = () => {
    setEditingId(null);
    setDraft(emptyDraft(orgCurrency));
  };

  const submit = () => {
    if (!draft) return;
    save.mutate({
      id: editingId,
      body: {
        label: draft.label || null,
        currency: draft.currency,
        fields: draft.fields,
        instructions: draft.instructions || null,
        enabled: draft.enabled,
        purpose,
      },
    });
  };

  // Validating as the manager types means the Save button tells the truth before it is pressed.
  const validation = draft ? validateBankAccount(draft.currency, draft.fields) : null;

  return (
    <Card>
      <CardTitle
        hint={COPY[purpose].live(enabledCount, accounts.length)}
        action={
          !readOnly && !draft ? (
            <Button size="sm" variant="secondary" onClick={startNew}>
              <IconPlus size={15} />
              {COPY[purpose].add}
            </Button>
          ) : null
        }
      >
        {step ? <span className="mr-2 text-ink-400">Step {step}</span> : null}
        {COPY[purpose].title}
      </CardTitle>

      {remove.errorMessage ? <Alert tone="danger">{remove.errorMessage}</Alert> : null}

      {isLoading ? (
        <Spinner />
      ) : accounts.length === 0 && !draft ? (
        <EmptyState
          title="No accounts yet."
          hint={COPY[purpose].empty}
          action={!readOnly ? <Button onClick={startNew}>{COPY[purpose].add}</Button> : undefined}
        />
      ) : (
        <ul className="space-y-3">
          {accounts.map((account) => (
            <li key={account.id}>
              {editingId === account.id && draft ? (
                <AccountForm
                  draft={draft}
                  setDraft={setDraft}
                  currencies={data?.currencies ?? supportedBankCurrencies()}
                  validation={validation}
                  errorMessage={save.errorMessage}
                  fieldErrors={save.fieldErrors}
                  pending={save.isPending}
                  onSave={submit}
                  onCancel={() => {
                    setDraft(null);
                    setEditingId(null);
                  }}
                />
              ) : (
                <AccountRow
                  account={account}
                  readOnly={readOnly}
                  busy={toggle.isPending || remove.isPending}
                  onToggle={(enabled) => toggle.mutate({ id: account.id, enabled })}
                  onEdit={() => startEdit(account)}
                  onRemove={() => {
                    if (window.confirm(`Remove ${summariseBankAccount(account)}? It disappears from every invoice and pay link.`)) {
                      remove.mutate(account.id);
                    }
                  }}
                />
              )}
            </li>
          ))}

          {draft && editingId === null ? (
            <li>
              <AccountForm
                draft={draft}
                setDraft={setDraft}
                currencies={data?.currencies ?? supportedBankCurrencies()}
                validation={validation}
                errorMessage={save.errorMessage}
                fieldErrors={save.fieldErrors}
                pending={save.isPending}
                onSave={submit}
                onCancel={() => setDraft(null)}
              />
            </li>
          ) : null}
        </ul>
      )}
    </Card>
  );
}

function AccountRow({
  account,
  readOnly,
  busy,
  onToggle,
  onEdit,
  onRemove,
}: {
  account: Account;
  readOnly: boolean;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const lines = bankAccountLines(account);

  return (
    <div
      className={`rounded-[var(--radius-surface)] border p-4 transition-colors ${
        account.enabled ? "border-ink-200 bg-white" : "border-ink-200 bg-ink-50"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`mt-0.5 shrink-0 ${account.enabled ? "text-brand-600" : "text-ink-400"}`}>
            <IconBank />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className={`font-semibold ${account.enabled ? "text-ink-900" : "text-ink-500"}`}>
                {account.label || summariseBankAccount(account)}
              </p>
              <Badge tone={account.enabled ? "success" : "muted"}>{account.currency}</Badge>
              {!account.enabled ? <Badge tone="muted">Hidden from payers</Badge> : null}
            </div>

            <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
              {lines.map((line) => (
                <div key={line.key} className="flex flex-wrap items-baseline gap-2">
                  <dt className="eyebrow">{line.label}</dt>
                  <dd className={`tabular text-sm ${account.enabled ? "text-ink-800" : "text-ink-500"}`}>{line.value}</dd>
                </div>
              ))}
            </dl>

            {account.instructions ? (
              <p className="mt-2 text-[13px] italic text-ink-500">“{account.instructions}”</p>
            ) : null}
          </div>
        </div>

        {!readOnly ? (
          <div className="flex shrink-0 items-center gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={account.enabled}
                disabled={busy}
                onChange={(event) => onToggle(event.target.checked)}
                className="size-4 rounded border-ink-300"
              />
              <span className={account.enabled ? "text-ink-700" : "text-ink-500"}>Show</span>
            </label>
            <Button size="sm" variant="secondary" onClick={onEdit}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemove} title="Remove this account">
              <IconTrash size={16} />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AccountForm({
  draft,
  setDraft,
  currencies,
  validation,
  errorMessage,
  fieldErrors,
  pending,
  onSave,
  onCancel,
}: {
  draft: Draft;
  setDraft: (draft: Draft) => void;
  currencies: string[];
  validation: ReturnType<typeof validateBankAccount> | null;
  errorMessage: string | null;
  fieldErrors: Record<string, string>;
  pending: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const spec = bankFieldsFor(draft.currency);
  // Only complain about a field once it has been touched and left — errors that appear while
  // someone is still typing the first character are noise.
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errorFor = (field: BankFieldSpec): string | undefined =>
    fieldErrors[field.key] ?? (touched[field.key] ? validation?.errors[field.key] : undefined);

  return (
    <div className="rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50/40 p-4">
      {errorMessage ? (
        <div className="mb-4">
          <Alert tone="danger">{errorMessage}</Alert>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Currency" htmlFor="account-currency" hint="Decides which details a payer needs.">
          <Select
            id="account-currency"
            value={draft.currency}
            onChange={(event) => {
              // Changing currency changes the field list, so anything that no longer applies is
              // dropped rather than silently carried along in the JSON.
              const next = event.target.value;
              const allowed = new Set(bankFieldsFor(next).map((field) => field.key));
              const kept = Object.fromEntries(Object.entries(draft.fields).filter(([key]) => allowed.has(key)));
              setDraft({ ...draft, currency: next, fields: kept });
            }}
          >
            {currencies.map((code) => (
              <option key={code} value={code}>
                {code} — {CURRENCIES[code]?.symbol ?? code}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Nickname" htmlFor="account-label" hint="For your team only — never shown to a payer.">
          <Input
            id="account-label"
            value={draft.label}
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
            placeholder="Main collection account"
          />
        </Field>

        {spec.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            htmlFor={`account-${field.key}`}
            required={field.required}
            hint={field.hint}
            error={errorFor(field)}
          >
            <Input
              id={`account-${field.key}`}
              value={draft.fields[field.key] ?? ""}
              inputMode={field.numeric ? "numeric" : undefined}
              maxLength={field.maxLength ? field.maxLength + 8 : undefined}
              placeholder={field.placeholder}
              onBlur={() => setTouched((current) => ({ ...current, [field.key]: true }))}
              onChange={(event) =>
                setDraft({ ...draft, fields: { ...draft.fields, [field.key]: event.target.value } })
              }
            />
          </Field>
        ))}

        <div className="sm:col-span-2">
          <Field
            label="Note to the payer"
            htmlFor="account-instructions"
            hint="Shown under this account on the pay link — “quote your invoice number”, “service charge only”."
          >
            <Textarea
              id="account-instructions"
              placeholder="Use the tenancy reference as the narration."
              rows={2}
              value={draft.instructions}
              onChange={(event) => setDraft({ ...draft, instructions: event.target.value })}
            />
          </Field>
        </div>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
          className="size-4 rounded border-ink-300"
        />
        Show this account to payers
      </label>

      <div className="mt-4 flex gap-2">
        <Button onClick={onSave} disabled={pending || !validation?.ok}>
          {pending ? "Saving…" : "Save account"}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {validation && !validation.ok ? (
        <p className="mt-2 text-xs text-ink-500">
          Still needed: {Object.values(validation.errors).join(", ")}.
        </p>
      ) : null}
    </div>
  );
}
